import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me, Metadata, MondayConnectionStatus, RoadmapSnapshot, Task } from '@mona-roadmap/domain';
import { api } from './api';
import { writesInFlight } from './inflight';

export const KEYS = {
  me: ['me'] as const,
  roadmap: ['roadmap'] as const,
  metadata: ['metadata'] as const,
  mondayStatus: ['monday-status'] as const,
  tasks: (deliverableId: string) => ['tasks', deliverableId] as const,
};

/**
 * Background refresh cadence. The API caches monday for 45 s per server, so polling at this rate costs at
 * most about one monday call per 45 s however many people have the app open, and hidden tabs do not poll.
 */
export const AUTO_REFRESH_MS = 45_000;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: 0, refetchOnWindowFocus: false, staleTime: 60_000 },
    },
  });
}

/**
 * Fetches the roadmap from monday (bypassing the server cache) and stores it. Used after our own writes
 * (another server instance might otherwise answer from a stale cache) and by the Refresh button.
 */
export async function refreshRoadmap(qc: QueryClient): Promise<RoadmapSnapshot> {
  const fresh = await api.roadmap(true);
  qc.setQueryData(KEYS.roadmap, fresh);
  return fresh;
}

export const useMe = () => useQuery<Me>({ queryKey: KEYS.me, queryFn: api.me, staleTime: Infinity });

/**
 * The roadmap stays current by itself: it refetches every {@link AUTO_REFRESH_MS} while the tab is visible,
 * when the tab regains focus (or the network returns) and the data is older than 20 s. A refresh never runs
 * (or is ignored) while one of our own saves is in flight, so optimistic edits are never clobbered.
 */
export function useRoadmap() {
  const qc = useQueryClient();
  return useQuery<RoadmapSnapshot>({
    queryKey: KEYS.roadmap,
    queryFn: async () => {
      const current = qc.getQueryData<RoadmapSnapshot>(KEYS.roadmap);
      if (current && writesInFlight() > 0) return current;
      return api.roadmap(false);
    },
    staleTime: 20_000,
    refetchInterval: () => (writesInFlight() > 0 ? false : AUTO_REFRESH_MS),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export const useMetadata = () => useQuery<Metadata>({ queryKey: KEYS.metadata, queryFn: api.metadata, staleTime: 5 * 60_000 });
export const useMondayStatus = (enabled: boolean) =>
  useQuery<MondayConnectionStatus>({ queryKey: KEYS.mondayStatus, queryFn: api.mondayStatus, enabled, staleTime: 30_000 });
export const useTasks = (deliverableId: string, enabled = true) =>
  useQuery<Task[]>({ queryKey: KEYS.tasks(deliverableId), queryFn: async () => (await api.tasks(deliverableId)).tasks, enabled, staleTime: 15_000 });
